/**
 * Editing a checklist — ROADMAP 5.27.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE EDITOR, TWO HOSTS
 *
 * The same list is edited in two places: seconds before an order, in the
 * confirmation, and at leisure on the strategy page. They had separate
 * implementations that already disagreed about what you could do — one
 * could reorder nothing, the other could not edit a line — so they are one
 * component now, and the hosts differ only in when they save.
 *
 * THE LIST IS THE TRADER'S. There is no "default" to revert to: the
 * shipped questions are a starting point written into their copy, and a
 * button offering to throw their edits away for ours had no business on
 * this screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY ROW HAS AN ID, AND THAT IS THE WHOLE TRICK
 *
 * The first version identified a row by `${index}:${text}`. Both halves of
 * that change constantly — the index on every reorder, the text on every
 * keystroke — so React saw a different element each time and remounted the
 * row underneath the gesture. Dragging fought a remount at every frame and
 * typing lost focus after one character. It read as "glitchy"; it was the
 * list dissolving and reforming while being used.
 *
 * So the editor keeps `{ id, text }` internally, with the id minted once
 * and never derived from anything that moves. Framer's `Reorder` then
 * animates a row it can actually follow, and an input keeps focus while
 * the thing behind it is rewritten.
 *
 * TEXT COMMITS ON BLUR, NOT ON KEYSTROKE.
 *
 * The confirmation saves to the server on every change this emits. Per
 * keystroke that is a PATCH per character, several in flight at once, and
 * the last one to land wins — which on a slow connection is not the last
 * one typed. Structural changes (add, remove, reorder) are deliberate and
 * commit at once; text commits when the field is left or Enter is pressed.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Reorder, useDragControls } from 'framer-motion'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { CHECKLIST_ITEM_MAX } from '@apna/shared'
import { cn } from '@/lib/utils'

export interface ChecklistEditorProps {
  items: readonly string[]
  /** The whole list, after whatever just changed. */
  onChange: (items: string[]) => void
  /** True while a save is in flight, so a second edit cannot race it. */
  busy?: boolean
  className?: string
  /**
   * What the empty box says. The strategy builder calls these RULES and
   * shows an example, because a blank field with a generic hint is the one
   * a trader leaves blank.
   */
  placeholder?: string
  /**
   * A word on the add button instead of a bare plus — ROADMAP 5.23.
   *
   * The icon alone is fine beside a list that already exists, and poor when
   * the list is EMPTY: there is nothing above it to say what would be
   * added, so the first rule is the hardest one to discover.
   */
  addLabel?: string
  addIcon?: ReactNode
}

interface Row {
  /** Stable for the row's whole life. Never derived from text or position. */
  readonly id: string
  text: string
}

let nextId = 0
const mintId = (): string => `chk-${(nextId += 1)}`
const rowsFrom = (items: readonly string[]): Row[] =>
  items.map((text) => ({ id: mintId(), text }))
const textsOf = (rows: readonly Row[]): string[] => rows.map((r) => r.text)
const same = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i])

export function ChecklistEditor({
  items,
  onChange,
  busy = false,
  className,
  placeholder = 'Add your own check…',
  addLabel,
  addIcon,
}: ChecklistEditorProps) {
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(items))
  const [draft, setDraft] = useState('')
  const draftBox = useRef<HTMLInputElement>(null)

  /*
   * What we last handed upwards. The host round-trips through a server and
   * hands the list back; without this the reply would re-seed the rows,
   * mint new ids, and remount everything a moment after each edit — the
   * same flicker the ids were introduced to remove.
   */
  const emitted = useRef<string[]>(textsOf(rows))

  useEffect(() => {
    if (same(items, emitted.current)) return
    // A genuinely different list: a different strategy, or someone else's
    // edit. Start again with fresh ids.
    emitted.current = [...items]
    setRows(rowsFrom(items))
  }, [items])

  const commit = useCallback(
    (next: Row[]) => {
      const texts = textsOf(next).map((t) => t.trim()).filter(Boolean)
      if (same(texts, emitted.current)) return
      emitted.current = texts
      onChange(texts)
    },
    [onChange],
  )

  const add = (): void => {
    const text = draft.trim()
    if (!text) return
    const next = [...rows, { id: mintId(), text }]
    setRows(next)
    setDraft('')
    commit(next)
  }

  return (
    <div className={className}>
      <Reorder.Group
        axis="y"
        values={rows}
        /*
         * Reordering is a finished gesture, so it commits at once. The
         * rows are the SAME objects framer handed back, which is what
         * lets it keep animating the one under the cursor.
         */
        onReorder={(next: Row[]) => {
          setRows(next)
          commit(next)
        }}
        className="space-y-1"
      >
        {rows.map((row, i) => (
          <RowItem
            key={row.id}
            row={row}
            busy={busy}
            onText={(text) =>
              setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, text } : r)))
            }
            onCommit={() => commit(rows)}
            onRemove={() => {
              const next = rows.filter((r) => r.id !== row.id)
              setRows(next)
              commit(next)
            }}
            testId={`checklist-row-${i}`}
            deleteTestId={`checklist-delete-${i}`}
          />
        ))}
      </Reorder.Group>

      <div className="mt-2 flex gap-2">
        <input
          ref={draftBox}
          value={draft}
          maxLength={CHECKLIST_ITEM_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder={placeholder}
          aria-label={addLabel ?? 'Add your own check'}
          data-testid="checklist-new-item"
          className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-[12px] outline-none focus:border-primary"
        />
        <button
          type="button"
          /*
           * EMPTY MEANS "PUT THE CURSOR HERE", not "nothing happens".
           *
           * Disabled, this button looks broken beside an empty box — which
           * is exactly the moment a trader is trying to add their FIRST
           * rule and has nothing above it to copy. Clicking it focuses the
           * field instead, so the button always does something and the
           * something is the next step.
           */
          onClick={() => (draft.trim() ? add() : draftBox.current?.focus())}
          disabled={busy}
          aria-label={addLabel ?? 'Add check'}
          data-testid="checklist-add-item"
          className={
            addLabel
              ? 'flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 text-[12px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-40'
              : 'flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40'
          }
        >
          {addIcon ?? <Plus size={14} />}
          {addLabel}
        </button>
      </div>
    </div>
  )
}

function RowItem({
  row,
  busy,
  onText,
  onCommit,
  onRemove,
  testId,
  deleteTestId,
}: {
  row: Row
  busy: boolean
  onText: (text: string) => void
  onCommit: () => void
  onRemove: () => void
  testId: string
  deleteTestId: string
}) {
  /*
   * `dragListener={false}` with explicit controls is what confines the
   * drag to the handle. Without it the row itself is draggable and
   * selecting a word in the text moves the item instead of the caret.
   */
  const controls = useDragControls()
  const [dragging, setDragging] = useState(false)

  return (
    <Reorder.Item
      value={row}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => setDragging(true)}
      onDragEnd={() => setDragging(false)}
      // A spring, not a duration: a dropped row settles instead of
      // stopping dead, which is the difference a hand feels.
      transition={{ type: 'spring', stiffness: 700, damping: 45, mass: 0.6 }}
      style={{ position: 'relative' }}
      className={cn(
        'flex items-center gap-1.5 rounded-lg border bg-muted/30 pl-1 pr-2',
        dragging
          ? 'z-10 border-primary/50 bg-card shadow-[0_10px_26px_rgb(15_23_42/14%)]'
          : 'border-border',
      )}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label={`Move "${row.text}"`}
        data-testid={`${testId}-handle`}
        onPointerDown={(e) => {
          // `touch-none` plus this keeps a touch drag from scrolling the
          // modal behind the list.
          e.preventDefault()
          controls.start(e)
        }}
        className="cursor-grab touch-none p-1 text-muted-foreground/60 transition-colors hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical size={13} />
      </button>

      {/*
        AN INPUT, NOT A LABEL WITH AN EDIT BUTTON. Fixing a typo in a check
        should cost one click, and the row already looks like a field.
      */}
      <input
        value={row.text}
        maxLength={CHECKLIST_ITEM_MAX}
        disabled={busy}
        onChange={(e) => onText(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        aria-label="Edit this check"
        data-testid={`${testId}-text`}
        className="h-8 flex-1 bg-transparent text-[12px] text-muted-foreground outline-none focus:text-foreground"
      />

      <button
        type="button"
        aria-label={`Remove "${row.text}"`}
        data-testid={deleteTestId}
        disabled={busy}
        onClick={onRemove}
        className="text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40"
      >
        <Trash2 size={13} />
      </button>
    </Reorder.Item>
  )
}
